import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { parseDurationSec as parseTimeLimitSeconds, formatTime } from '../../utils/time';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Stack,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FlagIcon from '@mui/icons-material/Flag';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import api from '../../api';
import { resolveOptionImageUrl } from '../../utils/mediaUrl';
import VideoExplanationPlayer from '../../components/VideoExplanationPlayer';

// ── Question type helpers (mirrors WorkSheetTake) ─────────────────────────────
const VOCAB_MATCH_COLORS = [
  { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8' },
  { bg: '#d1fae5', border: '#10b981', text: '#065f46' },
  { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  { bg: '#ede9fe', border: '#8b5cf6', text: '#5b21b6' },
  { bg: '#ffedd5', border: '#f97316', text: '#9a3412' },
  { bg: '#cffafe', border: '#06b6d4', text: '#155e75' },
  { bg: '#fecaca', border: '#ef4444', text: '#991b1b' },
];

const MATH_SYMBOLS = ['×', '÷', '²', '³', '√', '±', '≤', '≥', '≠', '≈', 'π', '∞', '°', '½', '¼', '¾'];

const getQuestionCategory = (qt = '') => {
  const t = (qt || '').toLowerCase();
  if (t.includes('writing') || t.includes('composition')) return 'writing';
  if ((t.includes('fill') && t.includes('blank')) || t === 'fill-in-the-blank') return 'fill_blank';
  if (t.includes('short answer') || t === 'short-answer' || t === 'short_answer') return 'short_answer';
  if (t.includes('vocab') || t.includes('match')) return 'vocab_matching';
  return 'mcq';
};

const getTypeBadgeProps = (qt = '') => {
  const t = (qt || '').toLowerCase();
  if (t.includes('writing') || t.includes('composition')) return { label: 'Writing Composition', color: '#5b21b6', bg: '#ede9fe' };
  if (t.includes('fill') && t.includes('blank')) return { label: 'Fill in the Blank', color: '#0369a1', bg: '#e0f2fe' };
  if (t.includes('vocab') || t.includes('match')) return { label: 'Vocabulary Matching', color: '#0f766e', bg: '#ccfbf1' };
  if (t.includes('short answer') || t === 'short-answer' || t === 'short_answer') return { label: 'Short Answer', color: '#92400e', bg: '#fef3c7' };
  if (t.includes('diagram')) return { label: 'Diagram Interpretation', color: '#1e40af', bg: '#dbeafe' };
  if (t.includes('multiple') || t === 'multiple-choice') return { label: 'Multiple Choice', color: '#166534', bg: '#dcfce7' };
  return qt ? { label: qt, color: '#374151', bg: '#f3f4f6' } : null;
};

const RETAKE_PAGE_SIZE = 1;

const WorkSheetSweepHistory = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const role = (localStorage.getItem('role') || '').toLowerCase();
  const isParent = role === 'parent';
  const prefix = isParent ? 'parent' : 'student';
  const basePath = isParent ? '/parent/ai-tests' : '/student/ai-tests';

  const stateTestName = location.state?.testName || '';

  // ── data state ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retakeHistory, setRetakeHistory] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [testName, setTestName] = useState(stateTestName);

  // ── sweep state ───────────────────────────────────────────────────────────
  const [swapeConfirmOpen, setSwapeConfirmOpen] = useState(false);
  const [pendingSwapeAttempt, setPendingSwapeAttempt] = useState(null);
  const [retakeSession, setRetakeSession] = useState(null);
  const [retakeAnswers, setRetakeAnswers] = useState({});
  const [retakeLoading, setRetakeLoading] = useState({});
  const [submittingRetake, setSubmittingRetake] = useState(false);
  const [retakeResult, setRetakeResult] = useState(null);
  const [activeSwapeMeta, setActiveSwapeMeta] = useState(null);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [flaggingQuestion, setFlaggingQuestion] = useState({});
  const [swapeTotalSeconds, setSwapeTotalSeconds] = useState(0);
  const [swapeTotalRemaining, setSwapeTotalRemaining] = useState(0);
  const [retakePage, setRetakePage] = useState(1);
  const [retakeTextAnswers, setRetakeTextAnswers] = useState({});
  const [retakeVocabMatches, setRetakeVocabMatches] = useState({});
  const [retakeSelectedVocabWord, setRetakeSelectedVocabWord] = useState({});
  const retakeShuffledDefsRef = useRef({});

  const autoSubmittedRef = useRef(false);
  const formatSeconds = formatTime;

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  // ── helpers ───────────────────────────────────────────────────────────────
  const makeAbsoluteUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    const trimmed = url.trim();
    const r2Match = trimmed.match(/^https?:\/\/pub-[^/]+\.r2\.dev\/(.+)$/);
    // Videos play straight from R2 (signed for students); the image proxy refuses them.
    if (r2Match && !/\.(mp4|webm|mov|m4v)(\?|$)/i.test(r2Match[1])) return `${(api.defaults.baseURL || '').replace(/\/$/, '')}/proxy/visual/${r2Match[1]}`;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return trimmed;
  };

  const getAttemptStats = (attempt) => {
    const summary = attempt?.summary ?? {};
    const answers = Array.isArray(attempt?.answers) ? attempt.answers : [];
    const rawTotal = summary.gradable_total ?? summary.total_questions ?? summary.total ?? summary.totalQuestions ?? summary.totalQuestionsCount;
    const totalQuestionsValue = typeof rawTotal === 'number' ? rawTotal : answers.length;
    const gradableTotal = summary.gradable_total ?? totalQuestionsValue;
    const safeGradable = gradableTotal || answers.length;
    const rawCorrect = summary.correct_answers ?? summary.correctAnswers;
    const correct = typeof rawCorrect === 'number' ? rawCorrect : answers.filter((a) => a?.is_correct).length;
    const incorrect = Math.max((safeGradable || 0) - correct, 0);
    return { totalQ: safeGradable, correct, incorrect };
  };

  const getAnswerType = (answer) => {
    if (answer?.answer_type) return answer.answer_type;
    if (answer?.matched_pairs) return 'vocab_matching';
    if (answer?.text_answer !== undefined) {
      const qt = (answer?.question_type || '').toLowerCase();
      if (qt.includes('writing') || qt.includes('composition')) return 'writing';
      if (qt.includes('fill') && qt.includes('blank')) return 'fill_blank';
      if (qt.includes('short')) return 'short_answer';
      return 'fill_blank';
    }
    return 'mcq';
  };

  // ── data fetching ─────────────────────────────────────────────────────────
  const isValidId = (id) => {
    if (id == null || id === '' || id === 'null' || id === 'undefined') return false;
    const n = Number(id);
    return Number.isFinite(n) && n > 0;
  };

  const fetchData = useCallback(async () => {
    if (!isValidId(testId)) {
      toast.error('Invalid work sheet. Please go back and try again.');
      navigate(`/${prefix}/ai-answer-sheet`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [retakeRes, resultsRes] = await Promise.all([
        api.get(`${basePath}/${testId}/retake-history`),
        api.get(`${basePath}/${testId}/results`),
      ]);
      const rh = retakeRes?.data?.data || retakeRes?.data || {};
      setRetakeHistory(rh);

      const rd = resultsRes?.data?.data || {};
      const results = rd?.results || [];
      const meta = rd?.test || {};
      const rawStatus = meta?.status;
      if (rawStatus !== undefined && rawStatus !== null && Number(rawStatus) !== 2) {
        toast.error('This work sheet is currently inactive.');
        navigate(`/${prefix}/ai-answer-sheet`);
        return;
      }
      if (meta.test_name || meta.name) setTestName(meta.test_name || meta.name);
      setAttempts(results);
    } catch (err) {
      const httpStatus = err?.response?.status;
      const message = err?.response?.data?.message || err?.response?.data?.error || 'Failed to load sweep history';
      if (httpStatus === 404 || httpStatus === 403) {
        toast.error('Work sheet not found.');
        navigate(`/${prefix}/ai-answer-sheet`);
        return;
      }
      if (String(message).toLowerCase().includes('inactive')) {
        toast.error('This work sheet is currently inactive.');
        navigate(`/${prefix}/ai-answer-sheet`);
        return;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [basePath, testId, navigate, prefix]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── sweep timer ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!retakeSession?.questions?.length) {
      setSwapeTotalSeconds(0); setSwapeTotalRemaining(0);
      setRetakePage(1);
      autoSubmittedRef.current = false;
      return undefined;
    }
    const totalSeconds = retakeSession.questions.reduce((sum, q) => {
      const raw = q?.time_limit_seconds ?? q?.time_limit ?? q?.time_limit_sec ?? q?.duration_in_sec ?? q?.question?.duration_in_sec ?? 0;
      return sum + parseTimeLimitSeconds(raw);
    }, 0);
    setSwapeTotalSeconds(totalSeconds);
    setSwapeTotalRemaining(totalSeconds);
    autoSubmittedRef.current = false;
    if (!totalSeconds) return undefined;
    const id = setInterval(() => setSwapeTotalRemaining((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [retakeSession?.token, retakeSession?.questions]);

  // auto-submit when timer runs out
  useEffect(() => {
    if (!retakeSession?.questions?.length) return;
    if (swapeTotalSeconds === 0 || swapeTotalRemaining > 0 || autoSubmittedRef.current || submittingRetake) return;
    autoSubmittedRef.current = true;
    handleSubmitRetake(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [swapeTotalRemaining, swapeTotalSeconds, retakeSession?.token, submittingRetake]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── paginated questions ───────────────────────────────────────────────────
  const paginatedRetakeQuestions = useMemo(() => {
    if (!retakeSession?.questions?.length) return { items: [], totalPages: 1 };
    const totalPages = Math.max(1, Math.ceil(retakeSession.questions.length / RETAKE_PAGE_SIZE));
    const safePage = Math.min(Math.max(retakePage, 1), totalPages);
    const startIndex = (safePage - 1) * RETAKE_PAGE_SIZE;
    return { items: retakeSession.questions.slice(startIndex, startIndex + RETAKE_PAGE_SIZE), totalPages };
  }, [retakeSession?.questions, retakePage]);

  // ── sweep handlers ────────────────────────────────────────────────────────
  const handleSwapeClick = (attempt) => { setPendingSwapeAttempt(attempt); setSwapeConfirmOpen(true); };
  const handleCancelSwape = () => { setSwapeConfirmOpen(false); setPendingSwapeAttempt(null); };

  const handleRetakeIncorrect = async (attempt) => {
    if (!attempt) return;
    const tid = attempt?.test_id || testId;
    // question_ids omitted — backend computes all eligible (answered-wrong + unanswered)
    const payload = { result_attempt_count: attempt.attempt_count };
    try {
      setRetakeLoading((prev) => ({ ...prev, [attempt.id]: true }));
      setRetakeResult(null);
      const response = await api.post(`${basePath}/${tid}/retake`, payload);
      const data = response?.data?.data;
      if (!data?.questions?.length) { toast.info('No questions available for Sweep.'); return; }
      const answerNumberMap = Array.isArray(attempt?.answers)
        ? attempt.answers.reduce((acc, a) => { if (a?.question_id) acc[a.question_id] = a.question_number ?? a.question_no ?? a.sequence; return acc; }, {})
        : {};
      const flaggedIds = data.questions.filter((q) => q.is_flagged_by_user).map((q) => q.question_id);
      setFlaggedQuestions(new Set(flaggedIds));
      setRetakeSession({
        token: data.retake_token,
        testId: data.test_id,
        questions: data.questions.map((q, i) => ({ ...q, displayNumber: answerNumberMap[q.question_id] ?? q.sequence ?? i + 1 })),
      });
      setActiveSwapeMeta({
        swapeSessionsUsed: data.swape_sessions_used,
        swapeSessionsRemaining: data.swape_sessions_remaining,
        maxSwapesPerUpload: data.max_swapes_per_upload,
        resultAttemptCount: data.result_attempt_count ?? attempt.attempt_count,
      });
      setRetakeAnswers(data.questions.reduce((acc, q) => { acc[q.question_id] = ''; return acc; }, {}));
      setRetakeTextAnswers({}); setRetakeVocabMatches({}); setRetakeSelectedVocabWord({});
      retakeShuffledDefsRef.current = {};
      toast.info(`${data.questions.length} incorrect questions ready for Sweep.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Unable to start Sweep');
    } finally {
      setRetakeLoading((prev) => { const next = { ...prev }; delete next[attempt.id]; return next; });
    }
  };

  const handleConfirmSwape = () => {
    setSwapeConfirmOpen(false);
    if (pendingSwapeAttempt) handleRetakeIncorrect(pendingSwapeAttempt);
    setPendingSwapeAttempt(null);
  };

  const handleRetakeAnswerChange = (questionId, letter) => {
    if (swapeTotalSeconds > 0 && swapeTotalRemaining === 0) { toast.warning('Sweep time is up.'); return; }
    setRetakeAnswers((prev) => ({ ...prev, [questionId]: letter }));
  };

  const handleFlagQuestion = async (questionId) => {
    if (!retakeSession?.testId) { toast.error('No active Sweep session.'); return; }
    const isFlagged = flaggedQuestions.has(questionId);
    try {
      setFlaggingQuestion((prev) => ({ ...prev, [questionId]: true }));
      let response;
      if (isFlagged) {
        response = await api.delete(`${basePath}/${retakeSession.testId}/questions/${questionId}/flag`, { data: {} });
        setFlaggedQuestions((prev) => { const next = new Set(prev); next.delete(questionId); return next; });
      } else {
        response = await api.post(`${basePath}/${retakeSession.testId}/questions/${questionId}/flag`, {});
        setFlaggedQuestions((prev) => new Set([...prev, questionId]));
      }
      toast.success(response?.data?.message || (isFlagged ? 'Question unflagged.' : 'Question flagged.'));
    } catch (err) {
      toast.error(err?.response?.data?.message || `Unable to ${isFlagged ? 'unflag' : 'flag'} question`);
    } finally {
      setFlaggingQuestion((prev) => { const next = { ...prev }; delete next[questionId]; return next; });
    }
  };

  const handleSubmitRetake = async () => {
    if (!retakeSession?.questions?.length) { toast.warning('No Sweep questions loaded.'); return; }
    const answers = retakeSession.questions.map((q) => {
      const qId = q.question_id;
      const category = getQuestionCategory(q.question_type || '');
      if (category === 'vocab_matching') {
        const pairs = retakeVocabMatches[qId] || {};
        const order = retakeShuffledDefsRef.current[qId] || [];
        if (!Object.keys(pairs).length) return null;
        return {
          question_id: qId, answer_type: 'vocab_matching',
          matched_pairs: Object.entries(pairs).map(([wordOptId, defPos]) => ({ word_option_id: parseInt(wordOptId, 10), matched_def_index: defPos })),
          shuffled_order: order,
        };
      }
      if (category === 'fill_blank' || category === 'short_answer' || category === 'writing') {
        const text = retakeTextAnswers[qId] || '';
        if (!text.trim() && category !== 'writing') return null;
        return { question_id: qId, answer_type: category, text_answer: text };
      }
      // MCQ
      const sel = retakeAnswers[qId];
      return sel ? { question_id: qId, answer_type: 'mcq', selected_letter: sel } : null;
    }).filter(Boolean);
    if (!answers.length) { toast.warning('Answer at least one question before submitting.'); return; }
    const payload = { retake_token: retakeSession.token, answers, result_attempt_count: activeSwapeMeta?.resultAttemptCount };
    try {
      setSubmittingRetake(true);
      const response = await api.post(`${basePath}/${retakeSession.testId}/retake/submit`, payload);
      const data = response?.data?.data;
      setRetakeResult(data || null);
      toast.success(response?.data?.message || 'Sweep submitted.');
      // Refresh retake history to show updated sessions
      const retakeRes = await api.get(`${basePath}/${testId}/retake-history`);
      setRetakeHistory(retakeRes?.data?.data || retakeRes?.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to submit Sweep');
    } finally {
      setSubmittingRetake(false);
    }
  };

  const handleClearRetake = () => {
    setRetakeSession(null); setRetakeAnswers({}); setRetakeResult(null); setActiveSwapeMeta(null);
    setFlaggedQuestions(new Set()); setRetakePage(1);
    setRetakeTextAnswers({}); setRetakeVocabMatches({}); setRetakeSelectedVocabWord({});
    retakeShuffledDefsRef.current = {};
  };

  // ── date formatter ────────────────────────────────────────────────────────
  const formatDate = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return ts; }
  };

  const formatToTwoDecimals = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0.00';
    return n.toFixed(2);
  };

  // ── loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Card sx={{ p: 4, textAlign: 'center', maxWidth: 440 }}>
          <Typography variant="h6" gutterBottom color="text.secondary">Failed to load</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{error}</Typography>
          <Button variant="contained" onClick={() => navigate(-1)}>Go Back</Button>
        </Card>
      </Box>
    );
  }

  const maxSwapes = retakeHistory?.max_swapes_per_upload || 4;
  const latestUsed = retakeHistory?.latest_swape_sessions_used || 0;
  const latestRemaining = retakeHistory?.latest_swape_sessions_remaining ?? (maxSwapes - latestUsed);
  const totalSessions = (retakeHistory?.by_upload_attempt || []).reduce((sum, u) => sum + (u.swape_sessions_used || 0), 0);
  const uploadCount = (retakeHistory?.by_upload_attempt || []).length;

  // Latest attempt ID — only show Sweep on this one
  const latestAttemptId = attempts.reduce((best, a) =>
    (a.attempt_count ?? 0) > (best?.attempt_count ?? -1) ? a : best, null)?.id;

  const isGlobalExpired = swapeTotalSeconds > 0 && swapeTotalRemaining === 0;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Back + header */}
      <Button startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 2 }} onClick={() => navigate(-1)}>
        Back
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ width: 5, height: 44, borderRadius: 1, background: 'linear-gradient(180deg, #667eea, #764ba2)', flexShrink: 0 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Sweep History</Typography>
          {testName && <Typography variant="body2" color="text.secondary">{testName}</Typography>}
        </Box>
        {!isParent && (
          <Button
            variant="contained"
            sx={{ ml: 'auto', background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)', borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
            onClick={() => navigate(`/${prefix}/ai-answer-sheet`, isValidId(testId) ? { state: { viewTestId: testId } } : undefined)}
          >
            Go to Work Sheet
          </Button>
        )}
      </Box>

      {/* Sweep Progress Tracker */}
      {retakeHistory && uploadCount > 0 ? (
        <Card sx={{ borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', boxShadow: '0 8px 32px rgba(102,126,234,0.35)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <AutoAwesomeIcon sx={{ opacity: 0.9 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Sweep Progress Tracker</Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2.5, opacity: 0.9 }}>
              Each answer sheet upload allows up to <strong>{maxSwapes}</strong> Sweep sessions to improve your score.
            </Typography>
            <Box sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>Latest Upload Sweep Usage</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 700 }}>{latestUsed} / {maxSwapes}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(latestUsed / maxSwapes) * 100}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
              />
            </Box>
            <Grid container spacing={2}>
              {[
                { label: 'Total Uploads', value: uploadCount, caption: 'answer sheets submitted' },
                { label: 'Sessions Used', value: `${latestUsed}/${maxSwapes}`, caption: 'for latest upload' },
                { label: 'Sweeps Remaining', value: latestRemaining, caption: 'for latest upload' },
                { label: 'Total Sessions', value: totalSessions, caption: 'across all uploads' },
              ].map(({ label, value, caption }) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.2)' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>{label}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>{caption}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ) : (
        !loading && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            No sweep sessions have been started for this work sheet yet.
          </Alert>
        )
      )}

      {/* Previous Attempts */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Previous Attempts</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {attempts.length} attempt{attempts.length !== 1 ? 's' : ''} for this work sheet.
          </Typography>
        </CardContent>
        <Divider />
        <CardContent sx={{ pt: 2 }}>
          {attempts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <Typography color="text.secondary">No attempts yet.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    {['Attempt', 'Date', 'Upload Score', 'Cumulative', 'Incorrect', 'Sweep Sessions', 'Engine', 'Actions'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: 'white', bgcolor: 'transparent !important', fontSize: '0.8rem' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attempts.map((attempt) => {
                    const summary = attempt.summary || {};
                    const stats = getAttemptStats(attempt);
                    const pct = typeof summary.percentage === 'number'
                      ? summary.percentage
                      : typeof summary.percent === 'number'
                        ? summary.percent
                        : (stats.totalQ ? Number(((stats.correct / stats.totalQ) * 100).toFixed(2)) : 0);
                    const correct = stats.correct;
                    const gradable = stats.totalQ;
                    const incorrect = stats.incorrect;

                    const uploadData = retakeHistory?.by_upload_attempt?.find(
                      (u) => u.upload_attempt_count === attempt.attempt_count
                    );
                    const sessionsUsed = uploadData?.swape_sessions_used || 0;
                    const sessionsRemaining = uploadData?.swape_sessions_remaining ?? (maxSwapes - sessionsUsed);
                    const swapeCorrected = uploadData?.swape_corrected_count || 0;
                    const cumulativePct = uploadData?.cumulative_percentage ?? pct;
                    const cumulativeCorrect = uploadData?.cumulative_correct_answers ?? correct;
                    const swapeSessions = uploadData?.swape_sessions || [];
                    const retakeInProgress = Boolean(retakeLoading[attempt.id]);
                    const isLatest = attempt.id === latestAttemptId;
                    const showSweep = !isParent && isLatest && incorrect > 0 && sessionsRemaining > 0;
                    const uploadPctText = formatToTwoDecimals(pct);
                    const cumulativePctText = formatToTwoDecimals(cumulativePct);
                    const incorrectText = formatToTwoDecimals(incorrect);

                    return (
                      <TableRow key={attempt.id} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                        <TableCell>
                          <Chip label={`Attempt ${attempt.attempt_count ?? '—'}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(attempt.created_at)}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, color: '#1a237e', fontSize: '0.9rem' }}>{uploadPctText}%</Typography>
                          <Typography variant="caption" color="text.secondary">{correct}/{gradable}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 800, color: Number(cumulativePctText) === 100 ? '#2e7d32' : '#1a237e', fontSize: '1rem' }}>
                            {cumulativePctText}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{cumulativeCorrect}/{gradable}</Typography>
                          {swapeCorrected > 0 && (
                            <Chip label={`+${swapeCorrected} via Sweep`} size="small" color="success" sx={{ mt: 0.5, display: 'block', width: 'fit-content', fontSize: '0.7rem' }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={incorrectText}
                            icon={incorrect > 0 ? <CancelIcon /> : <CheckCircleIcon />}
                            color={incorrect > 0 ? 'error' : 'success'}
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{sessionsUsed}/{maxSwapes} used</Typography>
                          {swapeSessions.length > 0 && (
                            <Stack spacing={0.5}>
                              {swapeSessions.map((session, idx) => (
                                <Chip
                                  key={session.retake_token}
                                  size="small"
                                  label={`Sweep ${idx + 1}: ${formatToTwoDecimals(session.percentage)}%`}
                                  color={session.percentage >= 50 ? 'success' : 'warning'}
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Stack>
                          )}
                          {sessionsRemaining > 0 && !isParent && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {sessionsRemaining} remaining
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{attempt.engine || '—'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {showSweep && (
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                disabled={retakeInProgress}
                                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, whiteSpace: 'nowrap' }}
                                onClick={() => handleSwapeClick(attempt)}
                              >
                                {retakeInProgress ? 'Starting…' : 'Sweep'}
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => navigate(`/${prefix}/work-sheet/${testId}/attempt/${attempt.id}/review`)}
                              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}
                            >
                              Review
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── SWEEP SESSION ─────────────────────────────────────────────────── */}
      {retakeSession && (
        <Box sx={{ mt: 4 }}>
          <Card sx={{ borderRadius: 3, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 4, height: 24, borderRadius: 1, background: 'linear-gradient(180deg, #667eea, #764ba2)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Sweep</Typography>
                  {retakeSession?.questions?.length > 0 && swapeTotalSeconds > 0 && (
                    <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: 15 }} />}
                      label={swapeTotalRemaining > 0 ? formatSeconds(swapeTotalRemaining) : 'Time up'}
                      color={swapeTotalRemaining === 0 ? 'error' : swapeTotalRemaining <= 30 ? 'error' : swapeTotalRemaining <= 120 ? 'warning' : 'default'}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={handleClearRetake} sx={{ borderRadius: 2 }}>Close</Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSubmitRetake}
                    disabled={submittingRetake || isGlobalExpired}
                    sx={{ borderRadius: 2, fontWeight: 700, background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }}
                  >
                    {submittingRetake ? 'Submitting…' : 'Submit Sweep'}
                  </Button>
                </Stack>
              </Box>

              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Sweep Session {(activeSwapeMeta?.swapeSessionsUsed ?? retakeHistory?.latest_swape_sessions_used ?? 0) + 1} of{' '}
                {activeSwapeMeta?.maxSwapesPerUpload ?? retakeHistory?.max_swapes_per_upload ?? 4} for this upload
                {isGlobalExpired && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Time has expired. This session counts as used.
                  </Typography>
                )}
              </Alert>

              {/* Question navigator */}
              {retakeSession.questions.length > 1 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fb', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1.5 }}>Jump to Question</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {retakeSession.questions.map((q, index) => {
                      const qPage = Math.ceil((index + 1) / RETAKE_PAGE_SIZE);
                      const isCurrent = qPage === retakePage;
                      const cat = getQuestionCategory(q.question_type || '');
                      const hasAnswer = cat === 'vocab_matching'
                        ? Object.keys(retakeVocabMatches[q.question_id] || {}).length > 0
                        : (cat === 'fill_blank' || cat === 'short_answer' || cat === 'writing')
                          ? !!(retakeTextAnswers[q.question_id] || '').trim()
                          : !!retakeAnswers[q.question_id];
                      return (
                        <Button
                          key={q.question_id}
                          size="small"
                          variant={isCurrent ? 'contained' : 'outlined'}
                          onClick={() => setRetakePage(qPage)}
                          sx={{
                            minWidth: 36, width: 36, height: 36, p: 0, fontSize: '0.78rem', borderRadius: 1.5,
                            fontWeight: isCurrent ? 700 : 500,
                            borderColor: hasAnswer ? '#4caf50' : '#9e9e9e',
                            color: isCurrent ? 'white' : hasAnswer ? '#2e7d32' : '#374151',
                            bgcolor: isCurrent ? 'primary.main' : hasAnswer ? '#e8f5e9' : 'transparent',
                            '&:hover': { opacity: 0.8 },
                          }}
                        >
                          {index + 1}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Questions */}
              <Stack spacing={2}>
                {paginatedRetakeQuestions.items.map((question) => {
                  const qId = question.question_id;
                  const category = getQuestionCategory(question.question_type || '');
                  const badge = getTypeBadgeProps(question.question_type || '');
                  const selectedLetter = retakeAnswers[qId] || '';

                  // ── Vocab matching state & logic ──────────────────────────
                  const opts = question.options || [];
                  if (category === 'vocab_matching' && !retakeShuffledDefsRef.current[qId]) {
                    const indices = opts.map((_, i) => i);
                    for (let i = indices.length - 1; i > 0; i--) {
                      const j = Math.floor(Math.random() * (i + 1));
                      [indices[i], indices[j]] = [indices[j], indices[i]];
                    }
                    retakeShuffledDefsRef.current[qId] = indices;
                  }
                  const shuffledIndices = retakeShuffledDefsRef.current[qId] || opts.map((_, i) => i);
                  const vocabMatches = retakeVocabMatches[qId] || {};
                  const selectedWordId = retakeSelectedVocabWord[qId] || null;
                  const selectedDefPos = retakeSelectedVocabWord[`${qId}_def`] ?? null;
                  const defPosToWordId = {};
                  Object.entries(vocabMatches).forEach(([wId, pos]) => { defPosToWordId[pos] = wId; });
                  const matchedWordIdList = Object.keys(vocabMatches);
                  const getMatchColor = (wordOptId) => { const idx = matchedWordIdList.indexOf(String(wordOptId)); return idx >= 0 ? VOCAB_MATCH_COLORS[idx % VOCAB_MATCH_COLORS.length] : null; };
                  const getMatchNumber = (wordOptId) => { const idx = matchedWordIdList.indexOf(String(wordOptId)); return idx >= 0 ? idx + 1 : null; };
                  const matchedCount = matchedWordIdList.length;
                  const allMatched = matchedCount === opts.length && opts.length > 0;
                  const hasVocabSelection = !!selectedWordId || selectedDefPos !== null;
                  const isCompact = opts.length >= 6;
                  const boxPy = isCompact ? 0.7 : 1.2;
                  const boxMb = isCompact ? 0.5 : 0.75;
                  const fontSize = isCompact ? '0.82rem' : '0.9rem';

                  const handleVocabWordClick = (optId) => {
                    if (isGlobalExpired) return;
                    const sId = String(optId);
                    if (selectedDefPos !== null) {
                      setRetakeVocabMatches((p) => { const cur = { ...(p[qId] || {}) }; Object.keys(cur).forEach((wId) => { if (cur[wId] === selectedDefPos) delete cur[wId]; }); delete cur[sId]; cur[sId] = selectedDefPos; return { ...p, [qId]: cur }; });
                      setRetakeSelectedVocabWord((p) => ({ ...p, [qId]: null, [`${qId}_def`]: null }));
                    } else {
                      setRetakeSelectedVocabWord((p) => ({ ...p, [qId]: p[qId] === sId ? null : sId, [`${qId}_def`]: null }));
                    }
                  };
                  const handleVocabDefClick = (shuffledPos) => {
                    if (isGlobalExpired) return;
                    if (selectedWordId) {
                      setRetakeVocabMatches((p) => { const cur = { ...(p[qId] || {}) }; Object.keys(cur).forEach((wId) => { if (cur[wId] === shuffledPos) delete cur[wId]; }); delete cur[selectedWordId]; cur[selectedWordId] = shuffledPos; return { ...p, [qId]: cur }; });
                      setRetakeSelectedVocabWord((p) => ({ ...p, [qId]: null, [`${qId}_def`]: null }));
                    } else {
                      setRetakeSelectedVocabWord((p) => ({ ...p, [`${qId}_def`]: p[`${qId}_def`] === shuffledPos ? null : shuffledPos, [qId]: null }));
                    }
                  };
                  const handleVocabUnmatch = (wordOptId) => {
                    setRetakeVocabMatches((p) => { const cur = { ...(p[qId] || {}) }; delete cur[String(wordOptId)]; return { ...p, [qId]: cur }; });
                    setRetakeSelectedVocabWord((p) => ({ ...p, [qId]: null, [`${qId}_def`]: null }));
                  };

                  // ── Render question input by type ─────────────────────────
                  const renderInput = () => {
                    if (category === 'writing') {
                      const minWords = question.ai_metadata?.min_words ?? 50;
                      const maxWords = question.ai_metadata?.max_words ?? 75;
                      const writingText = retakeTextAnswers[qId] || '';
                      const wCount = writingText.trim() ? writingText.trim().split(/\s+/).length : 0;
                      const wColor = wCount === 0 ? 'text.secondary' : wCount < minWords ? 'warning.main' : 'success.main';
                      return (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Writing Composition — write your response below
                            </Typography>
                            <Typography variant="caption" sx={{ color: wColor, fontWeight: 600 }}>
                              {wCount} / {minWords}–{maxWords} words
                            </Typography>
                          </Box>
                          <TextField multiline minRows={8} maxRows={20} fullWidth disabled={isGlobalExpired}
                            placeholder="Write your composition here…"
                            value={writingText}
                            onChange={(e) => setRetakeTextAnswers((p) => ({ ...p, [qId]: e.target.value }))}
                            sx={{ '& textarea': { lineHeight: 1.9 } }}
                          />
                          {wCount > 0 && wCount < minWords && (
                            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                              {minWords - wCount} more word{minWords - wCount !== 1 ? 's' : ''} needed for full marks
                            </Typography>
                          )}
                        </Box>
                      );
                    }

                    if (category === 'fill_blank') {
                      return (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Fill in the blank — type the missing word or phrase
                          </Typography>
                          <TextField fullWidth disabled={isGlobalExpired}
                            placeholder="Type your answer…"
                            value={retakeTextAnswers[qId] || ''}
                            onChange={(e) => setRetakeTextAnswers((p) => ({ ...p, [qId]: e.target.value }))}
                          />
                        </Box>
                      );
                    }

                    if (category === 'short_answer') {
                      const isMathsShort = (question.question_type || '').trim() === 'Short-answer' && (question.subject || '').toLowerCase().includes('math');
                      return (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Short Answer — write your answer below
                          </Typography>
                          {isMathsShort && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                              {MATH_SYMBOLS.map((sym) => (
                                <Box key={sym} onClick={() => !isGlobalExpired && setRetakeTextAnswers((p) => ({ ...p, [qId]: (p[qId] || '') + sym }))}
                                  sx={{ px: 1, py: 0.4, border: '1px solid #d1d5db', borderRadius: 1, cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'monospace', bgcolor: '#f9fafb', userSelect: 'none', '&:hover': { bgcolor: '#e0f2fe', borderColor: '#0369a1' } }}>
                                  {sym}
                                </Box>
                              ))}
                            </Box>
                          )}
                          <TextField multiline minRows={3} fullWidth disabled={isGlobalExpired}
                            placeholder="Write your answer…"
                            value={retakeTextAnswers[qId] || ''}
                            onChange={(e) => setRetakeTextAnswers((p) => ({ ...p, [qId]: e.target.value }))}
                          />
                        </Box>
                      );
                    }

                    if (category === 'vocab_matching') {
                      return (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Match all pairs</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: allMatched ? 'success.main' : 'text.secondary' }}>{matchedCount}/{opts.length} matched</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={opts.length > 0 ? (matchedCount / opts.length) * 100 : 0}
                            sx={{ mb: 1, height: 6, borderRadius: 3, bgcolor: '#e0f2fe', '& .MuiLinearProgress-bar': { bgcolor: allMatched ? 'success.main' : 'primary.main', borderRadius: 3 } }} />
                          <Box sx={{ textAlign: 'center', mb: 1.5, py: 0.75, px: 2, borderRadius: 2, bgcolor: hasVocabSelection ? '#eff6ff' : allMatched ? '#f0fdf4' : '#f8fafc', border: '1px solid', borderColor: hasVocabSelection ? '#bfdbfe' : allMatched ? '#bbf7d0' : '#e2e8f0', transition: 'all 0.2s' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: hasVocabSelection ? 'primary.main' : allMatched ? 'success.dark' : 'text.secondary' }}>
                              {allMatched ? 'All pairs matched! Review or click a pair to unmatch.' : hasVocabSelection ? (selectedWordId ? 'Now click the matching definition' : 'Now click the matching word') : 'Click a word or definition to start matching'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: isCompact ? 1.5 : 2, ...(opts.length > 8 ? { maxHeight: 500, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 } } : {}) }}>
                            {/* Words column */}
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.75, display: 'block', textAlign: 'center', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Words</Typography>
                              {opts.map((opt) => {
                                const sId = String(opt.option_id);
                                const isSelected = selectedWordId === sId;
                                const isMatched = sId in vocabMatches;
                                const color = isMatched ? getMatchColor(sId) : null;
                                const pairNum = isMatched ? getMatchNumber(sId) : null;
                                const isClickTarget = selectedDefPos !== null && !isMatched;
                                return (
                                  <Box key={opt.option_id} onClick={() => isMatched ? handleVocabUnmatch(opt.option_id) : handleVocabWordClick(opt.option_id)}
                                    sx={{ py: boxPy, px: 1.5, mb: boxMb, border: '2px solid', borderRadius: 2, cursor: isGlobalExpired ? 'not-allowed' : 'pointer', userSelect: 'none', textAlign: 'center', position: 'relative', borderColor: isSelected ? '#2563eb' : isMatched ? color.border : isClickTarget ? '#93c5fd' : '#e2e8f0', bgcolor: isSelected ? '#dbeafe' : isMatched ? color.bg : isClickTarget ? '#eff6ff' : 'background.paper', transition: 'all 0.2s ease', boxShadow: isSelected ? '0 0 0 3px rgba(37,99,235,0.2)' : isMatched ? `0 0 0 1px ${color.border}33` : 'none', '&:hover': { borderColor: isMatched ? color.border : '#2563eb', boxShadow: isMatched ? `0 0 0 2px ${color.border}44` : '0 0 0 3px rgba(37,99,235,0.15)', transform: 'translateY(-1px)' } }}>
                                    {isMatched && <Box sx={{ position: 'absolute', top: -8, left: -8, width: 20, height: 20, borderRadius: '50%', bgcolor: color.border, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>{pairNum}</Box>}
                                    <Typography sx={{ fontSize, fontWeight: isSelected || isMatched ? 700 : 500, color: isMatched ? color.text : isSelected ? '#1d4ed8' : 'text.primary' }}>{opt.text || opt.name}</Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                            {/* Definitions column */}
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.75, display: 'block', textAlign: 'center', color: '#047857', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Definitions</Typography>
                              {shuffledIndices.map((origIdx, shuffledPos) => {
                                const opt = opts[origIdx];
                                if (!opt) return null;
                                const matchedWordId = defPosToWordId[shuffledPos];
                                const isMatched = matchedWordId !== undefined;
                                const isDefSelected = selectedDefPos === shuffledPos;
                                const color = isMatched ? getMatchColor(matchedWordId) : null;
                                const pairNum = isMatched ? getMatchNumber(matchedWordId) : null;
                                const isClickTarget = !!selectedWordId && !isMatched;
                                return (
                                  <Box key={origIdx} onClick={() => { if (isMatched) handleVocabUnmatch(matchedWordId); else handleVocabDefClick(shuffledPos); }}
                                    sx={{ py: boxPy, px: 1.5, mb: boxMb, border: '2px solid', borderRadius: 2, cursor: isGlobalExpired ? 'not-allowed' : 'pointer', userSelect: 'none', textAlign: 'center', position: 'relative', borderColor: isDefSelected ? '#059669' : isMatched ? color.border : isClickTarget ? '#6ee7b7' : '#e2e8f0', bgcolor: isDefSelected ? '#d1fae5' : isMatched ? color.bg : isClickTarget ? '#ecfdf5' : '#f8fafc', transition: 'all 0.2s ease', boxShadow: isDefSelected ? '0 0 0 3px rgba(5,150,105,0.2)' : isMatched ? `0 0 0 1px ${color.border}33` : 'none', '&:hover': { borderColor: isMatched ? color.border : '#059669', boxShadow: isMatched ? `0 0 0 2px ${color.border}44` : '0 0 0 3px rgba(5,150,105,0.15)', transform: 'translateY(-1px)' } }}>
                                    {isMatched && <Box sx={{ position: 'absolute', top: -8, right: -8, width: 20, height: 20, borderRadius: '50%', bgcolor: color.border, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>{pairNum}</Box>}
                                    <Typography sx={{ fontSize, fontWeight: isDefSelected || isMatched ? 700 : 500, color: isMatched ? color.text : isDefSelected ? '#065f46' : 'text.primary' }}>{opt.match_text || opt.text || opt.name}</Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                          {matchedCount > 0 && !allMatched && (
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.disabled' }}>Click any coloured pair to unmatch it</Typography>
                          )}
                        </Box>
                      );
                    }

                    // Default: MCQ
                    return (
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup value={retakeAnswers[qId] || ''} onChange={(e) => handleRetakeAnswerChange(qId, e.target.value)}>
                          {opts.map((opt) => {
                            const isSelected = retakeAnswers[qId] === opt.letter;
                            return (
                              <FormControlLabel
                                key={opt.option_id}
                                value={opt.letter}
                                control={<Radio size="small" />}
                                disabled={isGlobalExpired}
                                label={<Box>
                                  <Typography component="span" sx={{ fontSize: '0.95rem' }}>
                                    <strong>{opt.letter}.</strong>{' '}
                                    {String(opt.text || opt.name || '').replace(/<[^>]*>/g, '').trim().toUpperCase() === String(opt.letter || '').trim().toUpperCase()
                                      ? ''
                                      : (opt.text || opt.name || 'Option')}
                                  </Typography>
                                  {resolveOptionImageUrl(opt) && (
                                    <Box
                                      component="img"
                                      src={resolveOptionImageUrl(opt)}
                                      alt=""
                                      sx={{ display: 'block', maxWidth: '100%', maxHeight: 160, objectFit: 'contain', mt: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}
                                    />
                                  )}
                                </Box>}
                                sx={{ mb: 1, border: '2px solid', borderColor: isSelected ? 'primary.main' : 'divider', borderRadius: 2, p: 1.5, mx: 0, bgcolor: isSelected ? 'primary.50' : 'transparent', transition: 'all 0.15s', '&:hover': { bgcolor: isSelected ? 'primary.50' : 'action.hover', borderColor: isSelected ? 'primary.main' : 'primary.light' } }}
                              />
                            );
                          })}
                        </RadioGroup>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Selected: <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>{selectedLetter || '—'}</Typography>
                        </Typography>
                      </FormControl>
                    );
                  };

                  return (
                    <Card key={qId} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        {/* Header row */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={`Question ${question.displayNumber ?? question.sequence ?? '—'}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                            {question.marks != null && (
                              <Chip
                                label={category === 'vocab_matching'
                                  ? `${question.options?.length ?? '?'} pairs = ${question.options?.length ?? '?'} marks`
                                  : `${question.marks} mark${question.marks !== 1 ? 's' : ''}`}
                                size="small"
                                sx={{ fontWeight: 600, bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}
                              />
                            )}
                            {flaggedQuestions.has(qId) && <Chip label="Flagged" size="small" color="warning" icon={<FlagIcon sx={{ fontSize: 14 }} />} sx={{ fontWeight: 600 }} />}
                          </Box>
                          <Button
                            size="small"
                            variant={flaggedQuestions.has(qId) ? 'contained' : 'outlined'}
                            color="warning"
                            startIcon={flaggingQuestion[qId] ? <CircularProgress size={14} color="inherit" /> : <FlagIcon />}
                            onClick={() => handleFlagQuestion(qId)}
                            disabled={isGlobalExpired || flaggingQuestion[qId]}
                            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                          >
                            {flaggingQuestion[qId] ? (flaggedQuestions.has(qId) ? 'Unflagging…' : 'Flagging…') : flaggedQuestions.has(qId) ? 'Unflag' : 'Flag Question'}
                          </Button>
                        </Stack>

                        {/* Type badge */}
                        {badge && (
                          <Chip label={badge.label} size="small" variant="filled"
                            sx={{ mb: 1.5, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em', bgcolor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30` }} />
                        )}

                        {/* Question text */}
                        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, fontWeight: 500, fontSize: '1rem' }}>
                          {question.question_text || '—'}
                        </Typography>

                        {/* Question image */}
                        {(question.visual_image_url || question.visual?.url || question.visual?.r2_url || question.visual?.public_url) && (() => {
                          const rawUrl = question.visual_image_url || question.visual?.url || question.visual?.r2_url || question.visual?.public_url;
                          const absUrl = makeAbsoluteUrl(rawUrl);
                          return absUrl ? (
                            <Box sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', maxHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
                              <img src={absUrl} alt="Question visual" style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                            </Box>
                          ) : null;
                        })()}

                        {/* Type-aware input */}
                        {renderInput()}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>

              {/* Pagination */}
              {retakeSession.questions.length > RETAKE_PAGE_SIZE && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Page {Math.min(retakePage, paginatedRetakeQuestions.totalPages)} of {paginatedRetakeQuestions.totalPages}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => setRetakePage((p) => Math.max(p - 1, 1))} disabled={retakePage <= 1} sx={{ borderRadius: 2 }}>Previous</Button>
                    <Button size="small" variant="contained" onClick={() => setRetakePage((p) => Math.min(p + 1, paginatedRetakeQuestions.totalPages))} disabled={retakePage >= paginatedRetakeQuestions.totalPages} sx={{ borderRadius: 2 }}>Next</Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Sweep result */}
          {retakeResult && (
            <Card sx={{ borderRadius: 3, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>Sweep Summary</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {(() => {
                    const s = retakeResult?.summary ?? {};
                    const writingCount = s.writing_count ?? 0;
                    const gradableTotal = s.gradable_total ?? s.total_questions ?? 0;
                    const gradableMarks = s.gradable_marks ?? null;
                    const totalMarks = s.total_marks ?? null;
                    return [
                      { label: 'Score', value: `${formatToTwoDecimals(s.percentage ?? 0)}%`, color: '#1a237e', bg: '#e8eaf6' },
                      { label: 'Correct', value: s.correct_answers ?? 0, color: '#2e7d32', bg: '#e8f5e9' },
                      {
                        label: gradableMarks != null ? (writingCount > 0 ? `Gradable Marks (${writingCount} writing)` : 'Gradable Marks') : (writingCount > 0 ? `Gradable (${writingCount} writing)` : 'Total'),
                        value: gradableMarks != null ? gradableMarks : gradableTotal,
                        color: '#374151', bg: '#f3f4f6',
                      },
                      ...(totalMarks != null && totalMarks !== gradableMarks ? [{ label: 'Total Marks', value: totalMarks, color: '#374151', bg: '#f3f4f6' }] : []),
                    ].map(({ label, value, color, bg }) => (
                      <Grid item xs={6} sm={4} md={3} key={label}>
                        <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: bg }}>
                          <Typography variant="h4" sx={{ fontWeight: 800, color }}>{value}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
                        </Box>
                      </Grid>
                    ));
                  })()}
                </Grid>
                {(retakeResult?.summary?.writing_count ?? 0) > 0 && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    Writing composition questions are not auto-graded and excluded from the score percentage.
                  </Alert>
                )}
                {retakeResult?.answers?.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto', maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {['#', 'Question', 'Type', 'Your Answer', 'Correct Answer', 'Status', 'Explanation'].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f8f9fb' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {retakeResult.answers.map((answer, idx) => {
                          const atype = getAnswerType(answer);
                          const isWriting = atype === 'writing';
                          const isVocab = atype === 'vocab_matching';
                          const isText = atype === 'fill_blank' || atype === 'short_answer';

                          const typeLabelMap = {
                            mcq: { label: 'MCQ', color: 'primary' },
                            fill_blank: { label: 'Fill Blank', color: 'info' },
                            short_answer: { label: 'Short Answer', color: 'secondary' },
                            vocab_matching: { label: 'Vocabulary', color: 'warning' },
                            writing: { label: 'Writing', color: 'default' },
                          };
                          const typeInfo = typeLabelMap[atype] || { label: atype, color: 'default' };

                          let yourAnswer, correctAnswer;
                          if (isWriting) {
                            yourAnswer = answer.text_answer
                              ? <Typography variant="body2" sx={{ maxWidth: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>{answer.text_answer}</Typography>
                              : <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>;
                            correctAnswer = <Typography variant="body2" color="text.disabled" fontStyle="italic">Manual review</Typography>;
                          } else if (isVocab) {
                            const pairs = answer.matched_pairs || [];
                            const total = answer.pairs_total ?? pairs.length;
                            const correct = answer.pairs_correct ?? pairs.filter((p) => p?.is_correct).length;
                            yourAnswer = (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{correct}/{total} pairs</Typography>
                                {pairs.length > 0 && (
                                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                    {pairs.map((p, pi) => (
                                      <Typography key={pi} variant="caption" sx={{ color: p?.is_correct ? '#2e7d32' : '#c62828' }}>
                                        {p?.word_text || `Word ${pi + 1}`} → {p?.def_text || `Def ${p?.matched_def_index ?? '?'}`}
                                      </Typography>
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            );
                            correctAnswer = answer.correct_pairs?.length
                              ? (
                                <Stack spacing={0.25}>
                                  {answer.correct_pairs.map((p, pi) => (
                                    <Typography key={pi} variant="caption" color="text.secondary">{p?.word_text || `Word ${pi + 1}`} → {p?.def_text || ''}</Typography>
                                  ))}
                                </Stack>
                              )
                              : <Typography variant="body2" color="text.disabled">—</Typography>;
                          } else if (isText) {
                            yourAnswer = answer.text_answer
                              ? <Typography variant="body2" sx={{ color: answer.is_correct ? '#2e7d32' : '#c62828', fontWeight: 600 }}>{answer.text_answer}</Typography>
                              : <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>;
                            correctAnswer = answer.correct_text
                              ? <Typography variant="body2" color="text.secondary">{answer.correct_text}</Typography>
                              : <Typography variant="body2" color="text.disabled">—</Typography>;
                          } else {
                            yourAnswer = <Typography variant="body2" sx={{ fontWeight: 700, color: answer.is_correct ? '#2e7d32' : '#c62828' }}>{answer.selected_letter || '—'}</Typography>;
                            correctAnswer = <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>{answer.correct_letter || '—'}</Typography>;
                          }

                          let statusChip;
                          if (isWriting) {
                            statusChip = <Chip size="small" label="Submitted" sx={{ fontWeight: 700, bgcolor: '#fff3e0', color: '#e65100' }} />;
                          } else if (answer.is_correct) {
                            statusChip = <Chip size="small" label="Correct" color="success" sx={{ fontWeight: 700 }} />;
                          } else {
                            statusChip = <Chip size="small" label="Incorrect" color="error" sx={{ fontWeight: 700 }} />;
                          }

                          return (
                            <TableRow key={answer.question_id || idx} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                              <TableCell>{answer.question_number || idx + 1}</TableCell>
                              <TableCell sx={{ maxWidth: 220 }}>
                                <Typography variant="body2" noWrap title={answer.question_text}>{answer.question_text || '—'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip size="small" label={typeInfo.label} color={typeInfo.color} variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
                              </TableCell>
                              <TableCell sx={{ minWidth: 120 }}>{yourAnswer}</TableCell>
                              <TableCell sx={{ minWidth: 120 }}>{correctAnswer}</TableCell>
                              <TableCell>{statusChip}</TableCell>
                              <TableCell sx={{ minWidth: 260 }}>
                                {answer.explanation || answer.explanation_video_url ? (
                                  <Box>
                                    {answer.explanation && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{answer.explanation}</Typography>}
                                    {answer.explanation_video_url && (
                                      <Box sx={{ mt: 1 }}>
                                        <VideoExplanationPlayer videoUrl={makeAbsoluteUrl(answer.explanation_video_url)} questionText={answer.question_text} variant="button" />
                                      </Box>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">Pending…</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">No answer details available for this Sweep session.</Alert>
                )}
                <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
                  <Button variant="outlined" onClick={handleClearRetake} sx={{ borderRadius: 2 }}>Close Sweep</Button>
                  <Button variant="contained" onClick={fetchData} sx={{ borderRadius: 2, fontWeight: 700, background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }}>
                    Refresh History
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* ── CONFIRM SWEEP DIALOG ──────────────────────────────────────────── */}
      <Dialog open={swapeConfirmOpen} onClose={handleCancelSwape} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Sweep Session</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>
            You have limited Sweep sessions per upload. Once started, this session counts towards your limit.
          </Alert>
          {pendingSwapeAttempt && (() => {
            const { incorrect } = getAttemptStats(pendingSwapeAttempt);
            const uploadData = retakeHistory?.by_upload_attempt?.find((u) => u.upload_attempt_count === pendingSwapeAttempt.attempt_count);
            const remaining = uploadData?.swape_sessions_remaining ?? maxSwapes;
            return (
              <>
                <Typography sx={{ mb: 1 }}>
                  Are you sure you want to start a Sweep session for <strong>{incorrect}</strong> incorrect question{incorrect !== 1 ? 's' : ''}?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sweeps remaining: <strong>{remaining}</strong> of {maxSwapes}
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCancelSwape} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleConfirmSwape} variant="contained" color="warning" sx={{ borderRadius: 2, fontWeight: 700 }}>
            Confirm Sweep
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkSheetSweepHistory;
